import React, { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";

const NARRATION_BY_EVENT_ID = Object.freeze({
  turing_1950: "history_1950_turing",
  dartmouth_1956: "history_1956_dartmouth",
  symbolic_rules_1960s_70s: "history_1960s_70s_rules",
  ai_winters_1970s_90s: "history_1970s_90s_ai_winters",
  expert_systems_1980s: "history_1980s_experts",
  deep_blue_1997: "history_1997_deep_blue",
  alexnet_2012: "history_2012_deep_learning",
  transformer_2017: "history_2017_transformers",
  generative_ai_2022: "history_2022_generative_ai",
  ai_today_2026: "history_2026_today",
});

export default function HistoryScreen({
  navigate,
  copy,
  language = "en",
  audioEnabled = false,
}) {
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const [index, setIndex] = useState(0);
  const events = copy.history.events;
  const event = events[index];

  const selectIndex = (nextIndex) => {
    const boundedIndex = Math.max(0, Math.min(events.length - 1, nextIndex));
    setIndex(boundedIndex);
    playNarration(NARRATION_BY_EVENT_ID[events[boundedIndex]?.id]);
  };

  return (
    <View style={{ padding: 28 }}>
      <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900", letterSpacing: 1.2 }}>
        {copy.history.eyebrow}
      </Text>
      <Text style={{ color: C.text, fontSize: 46, lineHeight: 52, fontWeight: "900", marginTop: 8 }}>
        {copy.history.title}
      </Text>
      <Text style={{ color: C.muted, fontSize: 19, lineHeight: 28, marginTop: 8, maxWidth: 900 }}>
        {copy.history.subtitle}
      </Text>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 24 }}>
        {events.map((item, itemIndex) => {
          const active = itemIndex === index;
          return (
            <Pressable
              key={`${item.year}-${item.short}`}
              onPress={() => selectIndex(itemIndex)}
              style={({ pressed }) => ({
                minHeight: 48,
                paddingHorizontal: 13,
                paddingVertical: 10,
                borderRadius: 999,
                borderWidth: active ? 2 : 1,
                borderColor: active ? C.accent : C.border,
                backgroundColor: active || pressed ? C.accentSoft : C.surfaceRaised,
              })}
            >
              <Text style={{ color: C.text, fontSize: 14, fontWeight: active ? "900" : "700" }}>
                {item.year}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <View style={{ marginTop: 22, padding: 28, borderRadius: 22, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised, minHeight: 250 }}>
        <Text style={{ color: C.accent, fontSize: 58, fontWeight: "900" }}>{event.year}</Text>
        <Text style={{ color: C.text, fontSize: 28, lineHeight: 36, fontWeight: "900", marginTop: 10 }}>{event.title}</Text>
        <Text style={{ color: C.muted, fontSize: 18, lineHeight: 29, marginTop: 14, maxWidth: 940 }}>{event.text}</Text>

        <View style={{ flexDirection: "row", gap: 12, marginTop: 24, flexWrap: "wrap" }}>
          <Pressable
            disabled={index === 0}
            onPress={() => selectIndex(index - 1)}
            style={({ pressed }) => ({
              minHeight: 52, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 14, borderWidth: 1,
              borderColor: C.border, backgroundColor: pressed ? C.accentSoft : C.surface, opacity: index === 0 ? 0.35 : 1,
            })}
          >
            <Text style={{ color: C.text, fontSize: 16, fontWeight: "900" }}>← {copy.history.previous}</Text>
          </Pressable>
          <Pressable
            disabled={index === events.length - 1}
            onPress={() => selectIndex(index + 1)}
            style={({ pressed }) => ({
              minHeight: 52, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 14, borderWidth: 1,
              borderColor: C.border, backgroundColor: pressed ? C.accentSoft : C.surface, opacity: index === events.length - 1 ? 0.35 : 1,
            })}
          >
            <Text style={{ color: C.text, fontSize: 16, fontWeight: "900" }}>{copy.history.next} →</Text>
          </Pressable>
        </View>
      </View>

      <Pressable
        onPress={() => navigate("history_drivers")}
        style={({ pressed }) => ({
          marginTop: 20, minHeight: 58, alignSelf: "flex-start", paddingHorizontal: 22, paddingVertical: 14,
          borderRadius: 16, borderWidth: 1, borderColor: C.accent, backgroundColor: pressed ? C.surfaceRaised : C.accentSoft,
        })}
      >
        <Text style={{ color: C.text, fontSize: 17, fontWeight: "900" }}>{copy.history.drivers} →</Text>
      </Pressable>
    </View>
  );
}
